/**
 * chart_view.ts — the shared CAMERA for the space charts (system chart M,
 * galactic chart G): an axonometric orbit view with mouse control.
 *
 *   wheel        zoom, anchored on the cursor (the point under the mouse
 *                stays put — the map-app feel)
 *   left-drag    rotate: yaw + elevation (pitch), clamped edge-on..top-down
 *   right-drag / shift-drag   pan
 *   double-click focus (the caller centers + zooms on what was hit)
 *   click        select (only when the pointer did not drag)
 *
 * Auto-orbit turns slowly until the first drag. Pure input + projection —
 * the charts own their drawing. Screen math: a point p projects to
 *   C + pan + [x', z'·sin(pitch) − y·cos(pitch)] · s
 * with x'/z' the yaw-rotated ground coordinates; z' doubles as depth.
 */
export interface ChartCallbacks {
  onClick(x: number, y: number): void;
  onDblClick(x: number, y: number): void;
}

export class ChartView {
  zoom = 1;
  yaw = 0.6;
  pitch = 0.62;
  panX = 0;
  panY = 0;
  autoOrbit = true;
  /** Canvas size in CSS px (square). */
  size = 560;
  readonly mouse = { x: -1, y: -1, inside: false };
  private drag: { x: number; y: number; btn: number; moved: boolean; shift: boolean } | null = null;
  private lastT = 0;
  private readonly zoomMin: number;
  private readonly zoomMax: number;
  private readonly orbitRate: number;

  constructor(zoomMin: number, zoomMax: number, orbitRate = 0.12) {
    this.zoomMin = zoomMin;
    this.zoomMax = zoomMax;
    this.orbitRate = orbitRate;
  }

  attach(cv: HTMLCanvasElement, cb: ChartCallbacks): void {
    const local = (e: MouseEvent): [number, number] => {
      const r = cv.getBoundingClientRect();
      return [(e.clientX - r.left) * (this.size / r.width), (e.clientY - r.top) * (this.size / r.height)];
    };
    cv.addEventListener('wheel', (e) => {
      e.preventDefault();
      const [mx, my] = local(e);
      this.zoomAt(mx, my, Math.exp(-e.deltaY * 0.0014));
    }, { passive: false });
    cv.addEventListener('pointerdown', (e) => {
      const [x, y] = local(e);
      this.drag = { x, y, btn: e.button, moved: false, shift: e.shiftKey };
      cv.setPointerCapture(e.pointerId);
    });
    cv.addEventListener('pointermove', (e) => {
      const [x, y] = local(e);
      this.mouse.x = x; this.mouse.y = y; this.mouse.inside = true;
      const d = this.drag;
      if (!d) return;
      const dx = x - d.x, dy = y - d.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
      if (d.btn === 0 && !d.shift) {
        this.yaw += dx * 0.008;
        this.pitch = Math.min(1.45, Math.max(0.1, this.pitch - dy * 0.005));
        if (d.moved) this.autoOrbit = false;
      } else {
        this.panX += dx; this.panY += dy;
      }
      d.x = x; d.y = y;
    });
    const end = (e: PointerEvent): void => {
      const d = this.drag;
      this.drag = null;
      if (d && !d.moved && d.btn === 0) { const [x, y] = local(e); cb.onClick(x, y); }
    };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', () => { this.drag = null; });
    cv.addEventListener('dblclick', (e) => { const [x, y] = local(e); cb.onDblClick(x, y); });
    cv.addEventListener('mouseleave', () => { this.mouse.inside = false; });
    cv.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** Zoom by `f` keeping the screen point (mx, my) fixed. */
  zoomAt(mx: number, my: number, f: number): void {
    const z2 = Math.min(this.zoomMax, Math.max(this.zoomMin, this.zoom * f));
    const ff = z2 / this.zoom;
    const c = this.size / 2;
    this.panX = (mx - c) - ((mx - c) - this.panX) * ff;
    this.panY = (my - c) - ((my - c) - this.panY) * ff;
    this.zoom = z2;
  }

  /** Center the screen point (sx, sy), then zoom by `mult` about the center. */
  focus(sx: number, sy: number, mult = 1): void {
    const c = this.size / 2;
    this.panX -= sx - c;
    this.panY -= sy - c;
    if (mult !== 1) this.zoomAt(c, c, mult);
    this.autoOrbit = false;
  }

  reset(): void {
    this.zoom = 1; this.yaw = 0.6; this.pitch = 0.62; this.panX = 0; this.panY = 0; this.autoOrbit = true;
  }

  /** Advance the auto-orbit. */
  tick(nowMs: number): void {
    const dt = this.lastT ? Math.min(0.1, (nowMs - this.lastT) / 1000) : 0;
    this.lastT = nowMs;
    if (this.autoOrbit && !this.drag) this.yaw += dt * this.orbitRate;
  }

  /** Project a world point; `s` = px per world unit. Returns [sx, sy, depth]. */
  proj(x: number, y: number, z: number, s: number): [number, number, number] {
    const cy = Math.cos(this.yaw), sy = Math.sin(this.yaw);
    const cp = Math.cos(this.pitch), sp = Math.sin(this.pitch);
    const rx = x * cy - z * sy;
    const rz = x * sy + z * cy;
    const c = this.size / 2;
    return [c + this.panX + rx * s, c + this.panY + (rz * sp - y * cp) * s, rz];
  }

  /** Screen-space direction of a world vector (for compass / heading). */
  dir(x: number, y: number, z: number): [number, number] {
    const [ax, ay] = this.proj(0, 0, 0, 1);
    const [bx, by] = this.proj(x, y, z, 1);
    const dx = bx - ax, dy = by - ay;
    const l = Math.hypot(dx, dy) || 1;
    return [dx / l, dy / l];
  }
}

/** A "nice" step (1/2/5 × 10^k) so ~`target` fits `span`. */
export function niceStep(span: number, target = 3): number {
  const raw = span / target;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return (m >= 5 ? 5 : m >= 2 ? 2 : 1) * p;
}

export function fmtKm(m: number): string {
  if (m >= 1_000_000) return `${(m / 1_000_000).toFixed(m >= 10_000_000 ? 0 : 1)} Mm`;
  if (m >= 1_000) return `${Math.round(m / 1000)} km`;
  return `${Math.round(m)} m`;
}

/** Deterministic 0..1 hash for chart speckle layouts. */
export function hash01(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 4294967296;
}
